import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "../store";
import { set } from "lodash";

interface Event {
	id: string;
	actual_items: string;
	items_validated?: boolean;
	[key: string]: any;
}

interface EventState {
	selectedEvent: Event | null;
	originalEvent: Event | null;
	isEditing: boolean;
}

const initialState: EventState = {
	selectedEvent: null,
	originalEvent: null,
	isEditing: false,
};

const eventSlice = createSlice({
	name: "event",
	initialState,
	reducers: {
		setSelectedEvent(state, action: PayloadAction<Event>) {
			state.selectedEvent = action.payload;
			state.originalEvent = { ...action.payload }; // snapshot
			state.isEditing = false;
		},
		clearSelectedEvent(state) {
			state.selectedEvent = null;
			state.originalEvent = null;
			state.isEditing = false;
		},
		setEventEditMode(state, action: PayloadAction<boolean>) {
			state.isEditing = action.payload;
		},
		updateEventField(
			state,
			action: PayloadAction<{ field: keyof Event; value: any }>
		) {
			if (!state.selectedEvent) return;
			try {
				set(
					state.selectedEvent,
					action.payload.field,
					action.payload.value
				);
			} catch (err) {
				console.warn(
					"Failed to update field:",
					action.payload.field,
					err
				);
			}
		},
		updateSelectedEventItems(state, action: PayloadAction<string>) {
			if (state.selectedEvent) {
				state.selectedEvent.actual_items = action.payload;
			}
		},
		resetEventEdits(state) {
			state.selectedEvent = state.originalEvent
				? { ...state.originalEvent }
				: null;
			state.isEditing = false;
		},
		saveEventEdits(state) {
			state.originalEvent = state.selectedEvent
				? { ...state.selectedEvent }
				: null;
			state.isEditing = false;
		},
	},
});

export const {
	setSelectedEvent,
	clearSelectedEvent,
	setEventEditMode,
	updateEventField,
	updateSelectedEventItems,
	resetEventEdits,
	saveEventEdits,
} = eventSlice.actions;

export const selectSelectedEvent = (state: RootState) =>
	state.event.selectedEvent;

export const selectOriginalEvent = (state: RootState) =>
	state.event.originalEvent;

export const selectEventEditMode = (state: RootState) => state.event.isEditing;

export default eventSlice.reducer;
